import { createSlice } from "@reduxjs/toolkit";

import { fetchGetMissionPlans } from "./actions";
import { DataStatus } from "../../types";

type MissionVersion = {
  id: string;
  name: string;
  version_number: number;
  time_stamp: string;
  updated_at: string;
};

type MissionPlansState = {
  data: Array<MissionAsset>;
  loadedSemanticMapIds: Array<string>;
  versions: Record<string, Array<MissionVersion>>; // masterMissionId -> versions
  status: DataStatus;
  error: string | null;
};

const initialState: MissionPlansState = {
  data: [],
  loadedSemanticMapIds: [],
  versions: {},
  status: DataStatus.idle,
  error: null,
};

export const Slice = createSlice({
  name: "missionPlans",
  initialState,
  reducers: {
    resetMissionPlans: () => initialState,
    addNewMissionPlan: (state, { payload }: { payload: MissionAsset }) => {
      // Check if mission already exists
      const exists = state.data.some(
        (mission: MissionAsset) => mission.id === payload.id
      );
      if (!exists) {
        state.data = [...state.data, payload];
      }
      return state;
    },
    updateMissionPlan: (state, { payload }: { payload: MissionAsset }) => {
      // For versioned missions, update by masterMissionId to handle version changes
      // For non-versioned missions, update by id
      const shouldUpdate = (existing: MissionAsset) => {
        // If new payload has a masterMissionId, match by masterMissionId
        if ((payload as any).masterMissionId) {
          return (
            (existing as any).masterMissionId ===
            (payload as any).masterMissionId
          );
        }
        // Otherwise match by id (for legacy missions or direct updates)
        return existing.id === payload.id;
      };

      state.data = state.data.map((missionPlan: MissionAsset) =>
        shouldUpdate(missionPlan) ? payload : missionPlan
      );
      return state;
    },
    updateMissionDeploymentTime: (
      state,
      {
        payload,
      }: {
        payload: {
          masterMissionId?: string;
          id: string;
          timeStamp: string;
          lastDeployedAnyVersion: string;
        };
      }
    ) => {
      // Update only deployment-related fields, preserving the rest of the mission data
      // This is used after deploying an older version to update deployment times
      // without replacing the latest version's updated_at timestamp
      state.data = state.data.map((missionPlan: MissionAsset) => {
        const matches = payload.masterMissionId
          ? (missionPlan as any).masterMissionId === payload.masterMissionId
          : missionPlan.id === payload.id;

        if (matches) {
          return {
            ...missionPlan,
            timeStamp: payload.timeStamp,
            lastDeployedAnyVersion: payload.lastDeployedAnyVersion,
          };
        }
        return missionPlan;
      });
      return state;
    },
    removeMissionPlan: (state, { payload }: { payload: string }) => {
      state.data = state.data.filter(
        (missionPlan: MissionAsset) => missionPlan.id !== payload
      );
      return state;
    },
    removeMissionVersion: (
      state,
      {
        payload,
      }: {
        payload: { masterMissionId: string; versionNumber: number };
      }
    ) => {
      const { masterMissionId, versionNumber } = payload;
      // Remove the specific version from the versions cache
      if (state.versions[masterMissionId]) {
        state.versions[masterMissionId] = state.versions[
          masterMissionId
        ].filter((v) => {
          const vNum = (v as any).versionNumber ?? v.version_number;
          return vNum !== versionNumber;
        });
        // If no versions remain in the cache, clean up the cache entry.
        // NOTE: Do NOT remove the mission from state.data here because the
        // cache can be stale (e.g., a new version was created after the cache
        // was populated). The thunk (fetchDeleteMissionVersion) always verifies
        // remaining versions via the API and handles mission removal.
        if (state.versions[masterMissionId].length === 0) {
          delete state.versions[masterMissionId];
        }
      }
      // When versions cache is not populated, the reducer cannot determine
      // whether other versions remain. The thunk (fetchDeleteMissionVersion)
      // handles this case by querying the API after deletion.
      return state;
    },
    setMissionVersions: (
      state,
      {
        payload,
      }: {
        payload: {
          masterMissionId: string;
          versions: Array<MissionVersion>;
        };
      }
    ) => {
      if (payload.versions.length > 0) {
        // Sort versions descending by version number (highest first) to match
        // the convention used by fetchGetMissionPlans.fulfilled. Consumers like
        // MissionItem and VersionSelector rely on cachedVersions[0] being the latest.
        const sortedVersions = [...payload.versions].sort((a: any, b: any) => {
          const aNum = (a as any).versionNumber || a.version_number || 0;
          const bNum = (b as any).versionNumber || b.version_number || 0;
          return bNum - aNum;
        });
        state.versions[payload.masterMissionId] = sortedVersions;
      } else {
        delete state.versions[payload.masterMissionId];
      }
      return state;
    },
    clearLoadedSemanticMapIds: (state) => {
      state.loadedSemanticMapIds = [];
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGetMissionPlans.pending, (state) => {
        state.status = DataStatus.loading;
        state.error = null;
      })
      .addCase(fetchGetMissionPlans.fulfilled, (state, action) => {
        state.status = DataStatus.succeeded;
        // Merge new missions with existing ones, avoiding duplicates
        const newMissions = action.payload.missions.filter(
          (newMission: MissionAsset) =>
            !state.data.some(
              (existingMission: MissionAsset) =>
                existingMission.id === newMission.id
            )
        );
        state.data = [...state.data, ...newMissions];
        state.loadedSemanticMapIds = state.loadedSemanticMapIds.concat(
          action.payload.semanticMapId as string
        );

        // Populate versions cache from all_versions field (handle both snake_case and camelCase)
        action.payload.missions.forEach((mission: any) => {
          const masterMissionId =
            mission.masterMissionId || mission.master_mission_id;
          const allVersions = mission.allVersions || mission.all_versions;
          if (masterMissionId && allVersions && Array.isArray(allVersions)) {
            // Sort versions by version_number descending (highest first) to ensure latest is index 0
            const sortedVersions = [...allVersions].sort((a: any, b: any) => {
              const aNum = a.versionNumber || a.version_number || 0;
              const bNum = b.versionNumber || b.version_number || 0;
              return bNum - aNum;
            });
            state.versions[masterMissionId] = sortedVersions;
          }
        });
      })
      .addCase(fetchGetMissionPlans.rejected, (state, action) => {
        state.status = DataStatus.failed;
        state.error = action.error.message ?? "";
      });
  },
});

export default Slice;
